import type { RequestDocument, Variables } from 'graphql-request';
import { GraphQLClient } from 'graphql-request';
import { createClient, type Client as SubscriptionClient } from 'graphql-ws';

let httpClient: GraphQLClient;
let wsClient: SubscriptionClient;
let tokenGetter: (() => Promise<string>) | null = null;
let roleGetter: (() => string) | null = null;
let userIdGetter: (() => string) | null = null;

interface GraphQLError {
  message: string;
  extensions?: {
    code?: string;
  };
}

interface GraphQLResponse<T> {
  data?: T;
  errors?: GraphQLError[];
}

class EnhancedGraphQLClient {
  private client: GraphQLClient;
  private tokenGetter: () => Promise<string>;
  private roleGetter: () => string;
  private userIdGetter: () => string;

  constructor(
    url: string,
    tokenGetter: () => Promise<string>,
    roleGetter: () => string,
    userIdGetter: () => string
  ) {
    this.client = new GraphQLClient(url);
    this.tokenGetter = tokenGetter;
    this.roleGetter = roleGetter;
    this.userIdGetter = userIdGetter;
  }

  private async getHeaders(): Promise<Record<string, string>> {
    const token = await this.tokenGetter();
    return {
      Authorization: `Bearer ${token}`,
      'X-Hasura-Role': this.roleGetter(),
      'x-hasura-user-id': this.userIdGetter(),
    };
  }

  private isJWTExpiredError(error: unknown): boolean {
    if (error && typeof error === 'object' && 'response' in error) {
      const response = (error as { response?: GraphQLResponse<unknown> }).response;
      if (response?.errors) {
        return response.errors.some(
          (err) =>
            err.message?.includes('Could not verify JWT: JWTExpired') ||
            err.extensions?.code === 'invalid-jwt'
        );
      }
    }
    return false;
  }

  async request<T, V extends Variables = Variables>(
    document: RequestDocument,
    variables?: V,
    maxRetries = 1
  ): Promise<T> {
    let lastError: unknown;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const headers = await this.getHeaders();
        this.client.setHeaders(headers);

        // @ts-expect-error graphql-request type signature expects rest parameters but accepts object
        return await this.client.request<T, V>(document, variables);
      } catch (error) {
        lastError = error;

        if (this.isJWTExpiredError(error) && attempt < maxRetries) {
          console.warn(
            `JWT expired during GraphQL request (attempt ${attempt + 1}/${maxRetries + 1}). Refreshing token and retrying...`
          );

          await this.tokenGetter();
          await new Promise((resolve) => setTimeout(resolve, 100));
          continue;
        }

        throw error;
      }
    }

    throw lastError;
  }
}

let enhancedClient: EnhancedGraphQLClient | null = null;

export const initializeGraphQLClient = (
  token: string,
  role: string,
  userDataParams: UserDataParams,
  getTokenFunction?: () => Promise<string>
) => {
  const httpUrl = import.meta.env.VITE_GRAPHQL_HTTP_URL;
  const wsUrl = import.meta.env.VITE_GRAPHQL_WS_URL;

  if (!httpUrl || !wsUrl) {
    throw new Error('GraphQL URL(s) are not set');
  }

  if (getTokenFunction) {
    tokenGetter = getTokenFunction;
  }
  roleGetter = () => role;
  userIdGetter = () => userDataParams.userId || '';

  httpClient = new GraphQLClient(httpUrl, {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Hasura-Role': role,
      'x-hasura-user-id': userDataParams.userId || '',
    },
  });

  if (tokenGetter && roleGetter && userIdGetter) {
    enhancedClient = new EnhancedGraphQLClient(httpUrl, tokenGetter, roleGetter, userIdGetter);
  }

  wsClient = createClient({
    url: wsUrl,
    connectionParams: {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Hasura-Role': role,
        'x-hasura-user-id': userDataParams.userId || '',
      },
    },
  });
};

export const getGraphQLClient = (): GraphQLClient | EnhancedGraphQLClient => {
  if (enhancedClient) {
    return enhancedClient;
  }

  if (!httpClient) {
    throw new Error('GraphQL Client has not been initialized. Call initializeGraphQLClient first.');
  }
  return httpClient;
};

export const getSubscriptionClient = () => {
  if (!wsClient) {
    throw new Error(
      'Subscription Client has not been initialized. Call initializeGraphQLClient first.'
    );
  }
  return wsClient;
};
